/**
 * MetricStrip Component
 *
 * Container for the three top metric cards:
 * Conduct Score (solid circle), Dissatisfaction Ratio, Volume of Feedback.
 *
 * Atomic level: Organism
 * Composes: ConductScoreMetricCard, DissatisfactionRatioCard, VolumeLineChartCard
 * Used by: TodayPage
 */

import React from 'react';
import { ConductScoreMetricCard } from './ConductScoreMetricCard';
import { DissatisfactionRatioCard } from './DissatisfactionRatioCard';
import { VolumeLineChartCard } from './VolumeLineChartCard';
import type { MetricStripProps } from '../types/today.types';

/**
 * Container for the three top metric cards:
 * - Conduct Score (solid colored circle)
 * - Dissatisfaction Ratio (donut chart)
 * - Volume of Feedback (donut chart)
 *
 * Responsive: Single column on mobile, 3 columns on tablet+
 */
export function MetricStrip({ metrics }: MetricStripProps): React.ReactElement {
  // Detect if there's no data (no feedback volume means no meaningful metrics)
  const noData = metrics.volumeOfFeedback.current === 0;

  if (noData) {
    return (
      <div className="rounded-2xl border border-border/70 bg-background/90 px-5 py-5 shadow-[var(--shadow-card)]">
        <div className="max-w-2xl space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Key metrics
          </p>
          <h3 className="font-jakarta text-xl font-extrabold tracking-[-0.03em] text-foreground">
            Waiting for the first records to arrive
          </h3>
          <p className="text-[13px] leading-[1.7] text-muted-foreground">
            Conduct score, dissatisfaction ratio, and feedback volume appear here once the selected window has activity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {/* Conduct Score - Solid colored circle */}
      <ConductScoreMetricCard
        value={metrics.conductScore.current}
        delta={metrics.conductScore.delta}
        trend={metrics.conductScore.trend}
      />

      {/* Dissatisfaction Ratio */}
      <DissatisfactionRatioCard
        value={metrics.dissatisfactionRatio.current}
        delta={metrics.dissatisfactionRatio.delta}
        trend={metrics.dissatisfactionRatio.trend}
      />

      {/* Volume of Feedback - Line Chart */}
      <VolumeLineChartCard
        value={metrics.volumeOfFeedback.current}
        delta={metrics.volumeOfFeedback.delta}
        trend={metrics.volumeOfFeedback.trend}
        hourlyData={metrics.volumeOfFeedback.hourlyData}
      />
    </div>
  );
}
